// TitleQ.OrderEntry.Web/ClientApp/src/app/multiOrderContactEditor/index.tsx
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/edit";
import StarIcon from "@mui/icons-material/Star";
import { Box, Button, CircularProgress, IconButton, Stack, TextField , Typography} from "@mui/material";
import React, { Fragment, useState } from "react";

import axios from "axios";
import { MRT_ColumnDef, MaterialReactTable } from "material-react-table";

import { Contact } from "../orderEntry/types/Contact";
import { InternalUser } from "../types/app";
import ContactTypeChooserModal from "./modals/ContactTypeChooserModal";
import EditBuyerSellerModal from "./modals/EditBuyerSellerModal";
import EditContactModal from "./modals/EditContactModal";
import EditUnderwriterModal from "./modals/EditUnderwriterModal";
import { convertContactToInternalUserSalesRep, getUniqueListOfSalesReps, isSalesRepsIdentical } from "./modals/helpers";
import SalesRepComparisonModal from "./modals/SalesRepComparisonModal";
import SalesRepModal from "./modals/SalesRepModal";
import StatusUpdateModal from "./modals/StatusUpdateModal";

const axiosNoInterceptor = axios.create();
const salesRepSlotCount = 3;

type OrderContacts = Record<string, Contact[]>;

type RowData = {
  orderNumber: string;
  buyer: React.JSX.Element;
  seller: React.JSX.Element;
  buyerAttorney: React.JSX.Element;
  sellerAttorney: React.JSX.Element;
  lenderAttorney: React.JSX.Element;
  lender: React.JSX.Element;
  mortgageBroker: React.JSX.Element;
  abstractors: React.JSX.Element[];
  underwriter: React.JSX.Element;
  buyerPrincipal: React.JSX.Element;
  buyerPrincipal2: React.JSX.Element;
  sellerPrincipal: React.JSX.Element;
  sellerPrincipal2: React.JSX.Element;
  buyerContact: React.JSX.Element;
  buyerContact2: React.JSX.Element;
  sellerContact: React.JSX.Element;
  sellerContact2: React.JSX.Element;
  salesReps: React.JSX.Element[];
};

type Underwriter = {
  payeeName: string;
  underwriterLookupCode: string;
  lookupCode: string;
};

type SalesRepComparisonModalState = {
  newContact: Contact;
  isAllSalesRepsIdentical: boolean;
  contactsReps: InternalUser[];
  previousSalesReps: InternalUser[];
};

const MultiOrderContactEditor = () => {
  const [orderInput, setOrderInput] = useState("");
  const [orderContacts, setOrderContacts] = useState<OrderContacts>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUnderwriterModalOpen, setIsUnderwriterModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [orderStatuses, setOrderStatuses] = useState<
    { orderNumber: string; status: "pending" | "done" | "error"; errorMessage?: string }[]
  >([]);

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContactType, setEditingContactType] = useState<{
    type: "Attorney" | string;
    label: string;
    index?: number;
  } | null>(null);
  const [isBuyerSellerModalOpen, setIsBuyerSellerModalOpen] = useState(false);
  const [isSalesRepModalOpen, setIsSalesRepModalOpen] = useState(false);
  const [buyerSellerType, setBuyerSellerType] = useState<"Buyer" | "Seller" | null>(null);
  const [currentlyUpdatingOrder, setCurrentlyUpdatingOrder] = useState<string | null>(null);
  const [isContactTypeChooserOpen, setIsContactTypeChooserOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);

  const [salesRepComparisonModalData, setSalesRepComparisonModalData] = useState<SalesRepComparisonModalState | null>(
    null
  );

  const resetSalesRepComparisonModalData = () => setSalesRepComparisonModalData(null);

  const rows: RowData[] = Object.entries(orderContacts).map(([orderNumber, contacts]) => {
    const findByType = (type: string) => contacts.find((c) => c.type === type);
    const findAttorneyByRep = (rep: string) => contacts.find((c) => c.type === "Attorney" && c.represents === rep);
    const getDisplayName = (c?: Contact) => {
      if (!c) return <></>;
      if(!c.name && !c.company) {
        return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic"  sx={{ opacity: 0.25 }}>
              Empty Contact
            </Typography>
        );
      }
      return (
        <>
          {c.name || c.company}
          {c.isMarketingSource && (
            <StarIcon fontSize="small" sx={{ color: "gold", ml: 0.5, verticalAlign: "middle" }} />
          )}
        </>
      );
    };



    const getContactSortNumber = (contact: Contact) =>
        contact.sortOrder ??
        parseInt(contact.code?.replace(/\D/g, "") || "0", 10);

    const findAllByType = (type: string) =>
        contacts
            .filter((c) => c.type === type)
            .sort((a, b) => {
              return getContactSortNumber(a) - getContactSortNumber(b);
            });


    const buyerPrincipals = findAllByType("Principal - Buyer");
    const sellerPrincipals = findAllByType("Principal - Seller");
    const buyerContacts = findAllByType("Buyer Contact");
    const sellerContacts = findAllByType("Seller Contact");
    const salesReps = findAllByType("Sales Person");
    
    return {
      orderNumber,
      buyer: getDisplayName(findByType("Buyer/Borrower")),
      seller: getDisplayName(findByType("Seller")),
      buyerAttorney: getDisplayName(findAttorneyByRep("BA")),
      sellerAttorney: getDisplayName(findAttorneyByRep("SA")),
      lenderAttorney: getDisplayName(findAttorneyByRep("L")),
      lender: getDisplayName(findByType("Lender")),
      mortgageBroker: getDisplayName(findByType("Mortgage Broker")),
      abstractors: findAllByType("Abstractor").map((abstractor) => (
        <Fragment key={`${orderNumber}-${abstractor.code || abstractor.lookupCode}`}>
          {getDisplayName(abstractor)}
          <br />
        </Fragment>
      )),
      underwriter: getDisplayName(findByType("Underwriter")),
      buyerPrincipal: getDisplayName(buyerPrincipals[0]),
      buyerPrincipal2: getDisplayName(buyerPrincipals[1]),
      sellerPrincipal: getDisplayName(sellerPrincipals[0]),
      sellerPrincipal2: getDisplayName(sellerPrincipals[1]),
      buyerContact: getDisplayName(buyerContacts[0]),
      buyerContact2: getDisplayName(buyerContacts[1]),
      sellerContact: getDisplayName(sellerContacts[0]),
      sellerContact2: getDisplayName(sellerContacts[1]),
      salesReps: salesReps.slice(0, salesRepSlotCount).map((rep, index) => (
        <Fragment key={`${orderNumber}-sales-rep-${rep.id || rep.lookupCode || index}`}>
          {getDisplayName(rep)}
          {index < salesRepSlotCount - 1 && <br />}
        </Fragment>
      ))
    };
  });

  const hasAnyValue = (rows: RowData[], key: keyof RowData) => {
    const isVisiblyEmpty = (val: any): boolean => {
      if (val == null || val === false) return true;

      if (typeof val === "string") return val.trim() === "";

      if (Array.isArray(val)) return val.every(isVisiblyEmpty);

      if (React.isValidElement(val)) {
        const type = val.type;

        if (type === React.Fragment || type === "br") {
          const children = (val as any).props?.children;
          if (!children) return true;
          return isVisiblyEmpty(children);
        }

        const children = (val as any).props?.children;
        return isVisiblyEmpty(children);
      }

      if (typeof val === "object") {
        return Object.keys(val).length === 0;
      }

      return false;
    };

    return rows.some((r) => !isVisiblyEmpty(r[key]));
  };


  const selectedRows = rows.filter((row) => rowSelection[row.orderNumber]);

  const attorneyRepForLabel = (label: string): string => {
    switch (label) {
      case "Buyer's Attorney":
        return "BA";
      case "Seller's Attorney":
        return "SA";
      case "Lender's Attorney":
        return "L";
      default:
        return "";
    }
  };

  const handleUpdateSalesReps = async (salesRepIds: string[]) => {
    const ordersToUpdate = selectedRows.map((r) => r.orderNumber);

    await updateOrdersInBatch(ordersToUpdate, async (orderNumber) => {
      await axiosNoInterceptor.post("/proxy/api/orders/SetSalesReps", {
        orderNumber,
        salesRepIds
      });
    });

    setIsSalesRepModalOpen(false);
  };

  const getOrderedSalesReps = (contacts: Contact[] = []) =>
    contacts
      .filter((contact) => contact.type === "Sales Person")
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const getSelectedSalesRepPrefill = () => {
    const selectedOrderNumbers = selectedRows.map((row) => row.orderNumber);
    if (!selectedOrderNumbers.length) return [null, null, null] as (InternalUser | null)[];

    const orderedRepsByOrder = selectedOrderNumbers.map((orderNumber) => getOrderedSalesReps(orderContacts[orderNumber]));
    const hasMatchingSalesReps = isSalesRepsIdentical(orderedRepsByOrder);
    if (!hasMatchingSalesReps) return [null, null, null] as (InternalUser | null)[];

    const matchingReps = orderedRepsByOrder[0] || [];
    return Array.from({ length: salesRepSlotCount }, (_, index) =>
      matchingReps[index] ? convertContactToInternalUserSalesRep(matchingReps[index]) : null
    );
  };

  const refreshOrderContacts = async (orderNumbers: string[]) => {
    try {
      const contactResponses = await Promise.all(
        orderNumbers.map(async (orderNumber) => {
          const { data: contacts }: { data: Contact[] } = await axiosNoInterceptor.get(
            `/api/orders/GetOrderContactsWithSalesReps?orderNumber=${orderNumber}`
          );
          return { orderNumber, contacts };
        })
      );

      setOrderContacts((prev) => {
        const updated = { ...prev };
        contactResponses.forEach(({ orderNumber, contacts }) => {
          updated[orderNumber] = contacts;
        });
        return updated;
      });
    } catch (err) {
      console.error("Failed to refresh contacts:", err);
    }
  };

  const handleClear = () => {
    setOrderInput("");
    setOrderContacts({});
    setRowSelection({});
    setOrderStatuses([]);
    setCurrentlyUpdatingOrder(null);
  };

  const handleAddOrder = async () => {
    const trimmedInput = orderInput.trim();
    if (!trimmedInput) return;

    setIsLoading(true);

    try {
      const { data: orderNumbers }: { data: string[] } = await axiosNoInterceptor.get(
        `/api/orders/GetOrderNumbersWithSubs?orderNumber=${trimmedInput}`
      );

      await refreshOrderContacts(orderNumbers);

      const newSelection: Record<string, boolean> = {};
      orderNumbers.forEach((orderNumber) => {
        newSelection[orderNumber] = true;
      });

      setRowSelection((prev) => ({ ...prev, ...newSelection }));
      setOrderInput("");
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrdersInBatch = async (orders: string[], updater: (orderNumber: string) => Promise<void>) => {
    const initialStatuses = orders.map((orderNumber) => ({
      orderNumber,
      status: "pending" as const
    }));
    setOrderStatuses(initialStatuses);
    setStatusModalOpen(true);

    for (const orderNumber of orders) {
      setCurrentlyUpdatingOrder(orderNumber); // ← set current
      try {
        await updater(orderNumber);
        setOrderStatuses((prev) => prev.map((s) => (s.orderNumber === orderNumber ? { ...s, status: "done" } : s)));
      } catch (err: any) {
        const errorMessage = err?.response?.data || err?.message || "Unknown error";
        setOrderStatuses((prev) =>
          prev.map((s) => (s.orderNumber === orderNumber ? { ...s, status: "error", errorMessage } : s))
        );
      }
    }

    setCurrentlyUpdatingOrder(null);
    await refreshOrderContacts(orders);
  };

  const handleEditClick = (contactInfo: { type: "Attorney" | string; label: string; index?: number; }) => {
    setIsAddMode(false);
    setEditingContactType(contactInfo);
    setIsContactModalOpen(true);
  };

  const handleNewMarketingSource = (newContact: Contact) => {
    const selectedOrdersNumbers = Object.entries(rowSelection)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    const selectedOrdersSalesReps = selectedOrdersNumbers.map((orderNumber) => {
      return getOrderedSalesReps(orderContacts[orderNumber]);
    });

    const isSalesRepsIdenticalOnOrders = isSalesRepsIdentical(selectedOrdersSalesReps);
    const uniqueSalesReps = getUniqueListOfSalesReps(selectedOrdersSalesReps).map(convertContactToInternalUserSalesRep);

    const marketingReps = (newContact?.marketingReps || []).map((m) => {
      return {
        lookupCode: m.name,
        fullName: m.fullName || m.displayName,
        selectId: m.id,
        salesRepId: m.id
      } as InternalUser;
    });

    setSalesRepComparisonModalData({
      newContact,
      isAllSalesRepsIdentical: isSalesRepsIdenticalOnOrders,
      contactsReps: marketingReps,
      previousSalesReps: uniqueSalesReps
    });
  };

  const handleSaveContact = async (
    newContact: Contact,
    isNewMarketingSource?: boolean,
    updatedSalesRepIds?: string[]
  ) => {
    if (isNewMarketingSource) {
      handleNewMarketingSource(newContact);
      return;
    }

    if (!editingContactType) return;

    setIsContactModalOpen(false);
    resetSalesRepComparisonModalData();
    const orders = selectedRows.map((r) => r.orderNumber);

    await updateOrdersInBatch(orders, async (orderNumber) => {
      const existingContacts = orderContacts[orderNumber] || [];


      const sameTypeContacts = existingContacts
          .filter((c) => c.type === editingContactType.type)
          .sort((a, b) => (a.sortOrder ?? parseInt(a.code?.replace(/\D/g, "") || "0", 10)) - (b.sortOrder ?? parseInt(b.code?.replace(/\D/g, "") || "0", 10)));

      const match =
          editingContactType.index != null
              ? sameTypeContacts[editingContactType.index]
              : sameTypeContacts[0];

      const isAttorney = editingContactType.type === "Attorney";
      const isOther =
          editingContactType.type === "Other" ||
          editingContactType.type.includes("Principal") ||
          editingContactType.type === "Buyer Contact" ||
          editingContactType.type === "Seller Contact";

      const represents = isAttorney
          ? attorneyRepForLabel(editingContactType.label)
          : undefined;


      const canonicalTypeMap: Record<string, string> = {
        "Buyer Principal 2": "Principal - Buyer",
        "Seller Principal 2": "Principal - Seller",
        "Buyer Contact 2": "Buyer Contact",
        "Seller Contact 2": "Seller Contact",
      };
      
      const contactType =
          canonicalTypeMap[editingContactType.label] ||
          (isAttorney
              ? editingContactType.label.replace("'", "")
              : isOther
                  ? editingContactType.type 
                  : editingContactType.type);

      const postBody = {
        orderNumber,
        ...(updatedSalesRepIds ? { updatedSalesRepIds } : {}),
        contact: {
          ...newContact,
          code: isAddMode ? undefined : match?.code,
          contactType,
          isOther,
          ...(isAttorney && { represents }),
          ...(match?.isMarketingSource ? { isMarketingSource: true } : {})
        }
      };

      if (newContact.isMarketingSource) {
        postBody.contact.isMarketingSource = true;
      }
      if (!isAddMode && match?.code) {
        await axiosNoInterceptor.post("/proxy/api/orders/updateOrderContact", postBody);
      } else {
        await axiosNoInterceptor.post("/proxy/api/orders/addOrderContact", postBody);
      }
    });

    setIsContactModalOpen(false);
  };

  const handleSaveUnderwriter = async (underwriter: Underwriter) => {
    const ordersToUpdate = selectedRows.map((r) => r.orderNumber);
    const titleCompany = Object.values(orderContacts)
      .find((contacts) => contacts?.some((c) => c.type === "Title Company"))
      ?.find((c) => c.type === "Title Company");

    if (!titleCompany?.lookupCode) return;

    await updateOrdersInBatch(ordersToUpdate, async (orderNumber) => {
      await axiosNoInterceptor.post("/proxy/api/orders/UpdateTitleCompanyAndUnderwriter", {
        orderNumber,
        titleCompanyLookupCode: titleCompany.lookupCode,
        underwriter: {
          underwriterLookupCode: underwriter.underwriterLookupCode,
          lookupCode: underwriter.lookupCode
        }
      });
    });

    setIsUnderwriterModalOpen(false);
  };

  const handleSaveSalesRepComparison = async (updatedSalesReps: InternalUser[]) => {
    if (!salesRepComparisonModalData?.newContact) {
      alert("No new contact selected"); ////I used an alert. Sue me
      return;
    }

    const updatedSalesRepIds = updatedSalesReps.map((rep) => rep?.salesRepId).filter(Boolean) as string[];
    await handleSaveContact(salesRepComparisonModalData.newContact, false, updatedSalesRepIds);
    resetSalesRepComparisonModalData();
  };

  const handleEditBuyerSeller = (type: "Buyer" | "Seller") => {
    setIsAddMode(false);
    setBuyerSellerType(type);
    setIsBuyerSellerModalOpen(true);
  };

  const handleSaveBuyerSeller = (data: any) => {
    console.log("Updated buyer/seller:", data);
    // TODO: Apply update to selected orders
  };

  const columns: MRT_ColumnDef<RowData>[] = [
    { accessorKey: "orderNumber", header: "Order #" },
    {
      id: "buyer",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Buyer
          <IconButton size="small" onClick={() => handleEditBuyerSeller("Buyer")}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.buyer
    },
    {
      id: "seller",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Seller
          <IconButton size="small" onClick={() => handleEditBuyerSeller("Seller")}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.seller
    },
    {
      id: "buyerAttorney",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Buyer's Attorney
          <IconButton size="small" onClick={() => handleEditClick({ type: "Attorney", label: "Buyer's Attorney" })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.buyerAttorney
    },
    {
      id: "sellerAttorney",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Seller's Attorney
          <IconButton size="small" onClick={() => handleEditClick({ type: "Attorney", label: "Seller's Attorney" })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.sellerAttorney
    },
    {
      id: "lenderAttorney",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Lender's Attorney
          <IconButton size="small" onClick={() => handleEditClick({ type: "Attorney", label: "Lender's Attorney" })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.lenderAttorney
    },
    {
      id: "mortgageBroker",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Mortgage Broker
          <IconButton
            size="small"
            onClick={() => handleEditClick({ type: "Mortgage Broker", label: "Mortgage Broker" })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.mortgageBroker
    },
    {
      id: "abstractors",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Abstractor
          <IconButton size="small" onClick={() => handleEditClick({ type: "Abstractor", label: "Abstractor" })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.abstractors
    },
    {
      id: "underwriter",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Underwriter
          <IconButton size="small" onClick={() => setIsUnderwriterModalOpen(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.underwriter
    },
    {
      id: "buyerPrincipal",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Principal - Buyer
          <IconButton
            size="small"
            onClick={() => handleEditClick({ type: "Principal - Buyer", label: "Buyer Principal", index: 0 })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.buyerPrincipal
    },
    {
      id: "buyerPrincipal2",
      header: (
          <Stack direction="row" spacing={1} alignItems="center">
            Principal - Buyer 2
            <IconButton
                size="small"
                onClick={() => handleEditClick({ type: "Principal - Buyer", label: "Buyer Principal", index: 1 })}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
      ) as any,
      accessorFn: (row) => row.buyerPrincipal2
    },
    {
      id: "sellerPrincipal",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Principal - Seller
          <IconButton
            size="small"
            onClick={() => handleEditClick({ type: "Principal - Seller", label: "Seller Principal", index: 0 })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.sellerPrincipal
    },
    {
      id: "sellerPrincipal2",
      header: (
          <Stack direction="row" spacing={1} alignItems="center">
            Principal - Seller 2
            <IconButton
                size="small"
                onClick={() => handleEditClick({ type: "Principal - Seller", label: "Seller Principal", index: 1 })}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
      ) as any,
      accessorFn: (row) => row.sellerPrincipal2
    },
    {
      id: "buyerContact",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Buyer Contact
          <IconButton size="small" onClick={() => handleEditClick({ type: "Buyer Contact", label: "Buyer Contact", index: 0 })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.buyerContact
    },
    {
      id: "buyerContact2",
      header: (
          <Stack direction="row" spacing={1} alignItems="center">
            Buyer Contact 2
            <IconButton size="small" onClick={() => handleEditClick({ type: "Buyer Contact", label: "Buyer Contact", index: 1 })}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
      ) as any,
      accessorFn: (row) => row.buyerContact2
    },
    {
      id: "sellerContact",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Seller Contact
          <IconButton size="small" onClick={() => handleEditClick({ type: "Seller Contact", label: "Seller Contact", index: 0 })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.sellerContact
    },
    {
      id: "sellerContact2",
      header: (
          <Stack direction="row" spacing={1} alignItems="center">
            Seller Contact 2
            <IconButton size="small" onClick={() => handleEditClick({ type: "Seller Contact", label: "Seller Contact", index: 1 })}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
      ) as any,
      accessorFn: (row) => row.sellerContact2
    },
    {
      id: "salesReps",
      header: (
        <Stack direction="row" spacing={1} alignItems="center">
          Sales Person
          <IconButton size="small" onClick={() => setIsSalesRepModalOpen(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) as any,
      accessorFn: (row) => row.salesReps
    }
  ];

  const visibleColumns = columns.filter((col) => {
    const key = (col.accessorKey || col.id) as keyof RowData;
    if (key === "orderNumber") return true;
    if (key === "salesReps") return true;
    return hasAnyValue(rows, key);
  });
  
  return (
    <>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} mb={2}>
          {rows.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsContactTypeChooserOpen(true)}
              disabled={selectedRows.length === 0}
            >
              Add Contact to Selected Orders
            </Button>
          )}

          {rows.length > 0 && (
            <Button variant="outlined" color="error" onClick={handleClear}>
              Clear All
            </Button>
          )}
        </Stack>
        <MaterialReactTable
          columns={visibleColumns}
          data={rows}
          enableStickyHeader
          enableRowSelection
          enableSelectAll
          enablePagination={false}
          getRowId={(row) => row.orderNumber}
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection }}
          muiTableContainerProps={{
            sx: {
              maxHeight: '70vh', 
            },
          }}
          mrtTheme={(theme) => ({
            selectedRowBackgroundColor: "rgba(0, 0, 0, 0)"
          })}
          muiTableBodyRowProps={{
            hover: false,
            sx: {
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0) !important" },
              "&:hover td": { backgroundColor: "rgba(0, 0, 0, 0) !important" },
              "&.MuiTableRow-hover": { backgroundColor: "rgba(0, 0, 0, 0) !important" },
              "&.MuiTableRow-hover td": { backgroundColor: "rgba(0, 0, 0, 0) !important" }
            }
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            variant="outlined"
            placeholder="Enter order number (e.g., 12345 or 12345-01)"
            value={orderInput}
            onChange={(e) => setOrderInput(e.target.value)}
            sx={{ width: 400 }}
          />
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            onClick={handleAddOrder}
            disabled={isLoading}
            sx={{ whiteSpace: "nowrap" }}
          >
            {isLoading ? "Loading..." : "Add another order"}
          </Button>
        </Stack>
      </Box>

      <EditUnderwriterModal
        open={isUnderwriterModalOpen}
        onClose={() => setIsUnderwriterModalOpen(false)}
        onSave={handleSaveUnderwriter}
        titleCompanyLookupCode={
          Object.values(orderContacts)
            .find((contacts) => contacts?.some((c) => c.type === "Title Company"))
            ?.find((c) => c.type === "Title Company")?.lookupCode
        }
      />

      <ContactTypeChooserModal
        open={isContactTypeChooserOpen}
        onClose={() => setIsContactTypeChooserOpen(false)}
        onSelect={(type) => {
          setIsAddMode(true);

          if (type === "Buyer" || type === "Seller") {
            setBuyerSellerType(type);
            setIsBuyerSellerModalOpen(true);
          } else if (type === "Buyer's Attorney" || type === "Seller's Attorney" || type === "Lender's Attorney") {
            setEditingContactType({ type: "Attorney", label: type });
            setIsContactModalOpen(true);
          } else if (type === "Underwriter") {
            setIsUnderwriterModalOpen(true);
          } else if (
            type === "Principal - Buyer" ||
            type === "Principal - Seller" ||
            type === "Buyer Contact" ||
            type === "Seller Contact"
          ) {
            setEditingContactType({ type, label: type });
            setIsContactModalOpen(true);
          } else {
            setEditingContactType({ type: "Other", label: type });
            setIsContactModalOpen(true);
          }
        }}
      />

      {isSalesRepModalOpen && (
        <SalesRepModal
          open={isSalesRepModalOpen}
          onClose={() => setIsSalesRepModalOpen(false)}
          onSave={handleUpdateSalesReps}
          initialSalesReps={getSelectedSalesRepPrefill()}
        />
      )}

      {Boolean(salesRepComparisonModalData) && (
        <SalesRepComparisonModal
          open={true}
          isAllSalesRepsIdentical={salesRepComparisonModalData?.isAllSalesRepsIdentical}
          contactsReps={salesRepComparisonModalData?.contactsReps}
          previousSalesReps={salesRepComparisonModalData?.previousSalesReps}
          onClose={() => resetSalesRepComparisonModalData()}
          onSave={handleSaveSalesRepComparison}
        />
      )}

      {editingContactType && (
        <EditContactModal
          open={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          onSave={handleSaveContact}
          title={`Edit ${editingContactType.label}`}
          label={editingContactType.label}
          contactType={editingContactType.type}
        />
      )}

      <StatusUpdateModal
        open={statusModalOpen}
        orderStatuses={orderStatuses}
        title="Updating Orders"
        onClose={() => setStatusModalOpen(false)}
        currentlyUpdatingOrder={currentlyUpdatingOrder}
      />

      <EditBuyerSellerModal
        open={isBuyerSellerModalOpen}
        onClose={() => setIsBuyerSellerModalOpen(false)}
        onSave={handleSaveBuyerSeller}
        contactType={buyerSellerType || "Buyer"}
      />
    </>
  );
};

export default MultiOrderContactEditor;
